import { useEffect, useRef } from 'react'
import { useTranslation } from '@/i18n'
import { useAppStore } from '@/store'
import FeedbackPromptBubble from './FeedbackPromptBubble'
import HelpAgentMessageBubble from './HelpAgentMessageBubble'
import ThinkingIndicator from './ThinkingIndicator'

/**
 * Scrollable conversation body. Starts with the greeting bubble
 * ("Hi {firstName}, how can I help you?"), auto-scrolls to the newest
 * message, and appends the feedback prompt once an answer completes.
 */
const HelpAgentMessages = () => {
  const { t } = useTranslation('help_agent')
  const helpMessages = useAppStore((s) => s.helpMessages)
  const helpToolActivity = useAppStore((s) => s.helpToolActivity)
  const helpFeedbackOffered = useAppStore((s) => s.helpFeedbackOffered)
  const helpIsStreaming = useAppStore((s) => s.helpIsStreaming)
  const firstName = useAppStore((s) => s.user?.given_name || s.user?.name?.split(' ')[0])

  const bottomRef = useRef<HTMLDivElement>(null)
  const lastMessage = helpMessages[helpMessages.length - 1]
  useEffect(() => {
    // Optional-call guard: jsdom doesn't implement scrollIntoView.
    bottomRef.current?.scrollIntoView?.({ block: 'end' })
  }, [helpMessages.length, lastMessage?.content, helpFeedbackOffered])

  return (
    <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-4">
      <div className="flex justify-start">
        <div className="max-w-[85%] rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-900 dark:bg-dark-accent dark:text-dark-text">
          {firstName ? t('greeting', { firstName }) : t('greeting_generic')}
        </div>
      </div>
      {helpMessages.map((message) =>
        // The streaming assistant placeholder stays invisible until its
        // first token — the thinking indicator below owns that gap.
        message.status === 'streaming' && message.content === '' ? null : (
          <HelpAgentMessageBubble key={message.id} message={message} />
        )
      )}
      {helpIsStreaming &&
        (helpToolActivity !== null ||
          (lastMessage?.role === 'assistant' && lastMessage.content === '')) && (
          <ThinkingIndicator toolActivity={helpToolActivity} />
        )}
      {helpFeedbackOffered && !helpIsStreaming && <FeedbackPromptBubble />}
      <div ref={bottomRef} />
    </div>
  )
}

export default HelpAgentMessages
